import type { CalendarDate } from '@internationalized/date'
import { parseDate, type DateValue } from '@internationalized/date'
import { DateTime } from 'luxon'

/**
 * Converts a MM/DD/YYYY string to a UTC timestamp (milliseconds since epoch).
 * Returns null if the input is not valid.
 */
export function mdyToTimestamp(mdy: string): number | null {
	const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(mdy.trim())
	if (!m) return null
	const month = Number(m[1])
	const day = Number(m[2])
	const year = Number(m[3])
	return Date.UTC(year, month - 1, day)
}

/**
 * Calculates age (as a string) from a CalendarDate object.
 * Returns an empty string if the date is invalid or out of range.
 */
export function ageFromCalendarDate(d: CalendarDate | null): string {
	if (!d) return ''
	const today = new Date()
	let age = today.getFullYear() - d.year
	const beforeBirthday =
		today.getMonth() + 1 < d.month || (today.getMonth() + 1 === d.month && today.getDate() < d.day)
	if (beforeBirthday) age -= 1
	if (age < 0 || age > 120) return ''
	return String(age)
}

/**
 * Checks if a string is in MM/DD/YYYY format.
 */
export const isMDY = (s?: string) => !!s && /^\d{2}\/\d{2}\/\d{4}$/.test(s)
/**
 * Checks if a string is in ISO YYYY-MM-DD format.
 */
export const isISO = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s)

/**
 * Converts a MM/DD/YYYY string to a CalendarDate object.
 * Returns null if the input is not valid.
 */
export function mdyToCalendarDate(mdy?: string | null): CalendarDate | null {
	if (!mdy || !isMDY(mdy)) return null
	const [mm, dd, yyyy] = mdy.split('/')
	try {
		return parseDate(`${yyyy}-${mm}-${dd}`)
	} catch {
		return null
	}
}

/**
 * Converts an ISO YYYY-MM-DD string to a CalendarDate object.
 * Returns null if the input is not valid.
 */
export function isoToCalendarDate(iso?: string | null): CalendarDate | null {
	if (!iso || !isISO(iso)) return null
	try {
		return parseDate(iso)
	} catch {
		return null
	}
}

/**
 * Converts a string in either MM/DD/YYYY or ISO format to a CalendarDate object.
 * Returns null if the input is not valid.
 */
export function anyToCalendarDate(s?: string | null): CalendarDate | null {
	if (!s) return null
	if (isMDY(s)) return mdyToCalendarDate(s)
	if (isISO(s)) return isoToCalendarDate(s)
	return null
}

/**
 * Converts a CalendarDate or DateValue object to a MM/DD/YYYY string.
 * Returns an empty string if the input is not valid.
 */
export function calendarDateToMDY(val: DateValue | null): string {
	if (!val) return ''
	const mm = String(val.month).padStart(2, '0')
	const dd = String(val.day).padStart(2, '0')
	const yyyy = String(val.year)
	return `${mm}/${dd}/${yyyy}`
}

/**
 * Calculates age (as a string) from a MM/DD/YYYY string using Luxon.
 * Returns an empty string if the input is not valid.
 */
export function computeAgeFromMDY(mdy: string): string {
	const dt = DateTime.fromFormat(mdy, 'MM/dd/yyyy')
	if (!dt.isValid) return ''
	const years = Math.floor(DateTime.now().diff(dt, 'years').years)
	return years >= 0 ? String(years) : ''
}
